import React, { useCallback, useEffect, useMemo, useReducer, useState } from 'react'
import { mapToSeatUsage, MapToOrgPlanHistory, NewOrganization as NewOrg, OrganizationModel, OrgPlanHistory, SeatUsage } from '../../models/organization.model';
import { RechargeStatus } from '../../models/transaction-history.model';
import { PlanType } from '../../models/plans.model';
import chargebeeService from '../../services/chargebee.service';
import { useSelector } from 'react-redux';
import * as selector from "../../selectors/selectors"
import { LoadNewOrganizations, LoadOrganizations } from '../../actions';
import { useDispatch } from 'react-redux';
import { ColumnApi, GridApi, ICellRendererParams, ValueFormatterParams, ColDef, ValueGetterParams } from 'ag-grid-community';
import { Link, useNavigate } from 'react-router-dom';
import { AppRoutes } from '../routes/route.path';
import { UtilityService } from '../../services/UtilityService';
import { StatusRenderer } from '../shared/grid/statusRenderer';
import { ActionLabels, appNote, buttonLabels, emptyLabels, GridStatusLabelVariant, pageTitles, ToasterTexts } from '../shared/constants';
import { dateComparator, HeaderTemplate } from "../shared/grid/grid.services";
import { ActionsRenderer } from '../shared/grid/actionsRenderer';
import { AddOrgIcon, CancelIcon, CancelSubscriptionIcon, DisableIcon, EditIcon, EnableIcon, PlanDetailsIcon, RechargeIcon, ScheduleDailyIcon, ScheduleMonthlyIcon, SettingsIcon, TransactionHistoryIcon } from '../../icon';
import GridComponent from '../shared/grid';
import ViewOrganization from '../organizations/organization-view.component';
import OrgSettings from '../organizations/org-settings.component';
import ConfirmDialog from '../shared/confirm-dialog.component';
import { OrganizationService } from '../../services/OrganizationService';
import setToast from '../../helpers/toaster.helper';
import { ToasterType } from '../shared/toaster/toaster.model';
import { Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Popover, Tooltip } from '@mui/material';
import { MenuItemRenderer } from "../shared/grid/actionsMenuItemRendrer";
import PageHeaderComponent from '../shared/page-header.component';
import { TypeRenderer } from '../shared/grid/typeRendrer';
import { setHeaderCount, setHeaderTitle, setRefreshHandler } from '../../helpers/header.helper';
import ButtonComponent from '../shared/button/button.component';
import { CustomFilterCategory, CustomFilterOption } from '../shared/filters/filter.model';
import FilterComponent from '../shared/filters/filter.component';
import _ from 'lodash';

const getCycleValue = (row: NewOrg) =>
    row.IsYearPlan === true ? "Yearly" : row.SeatUsage?.length > 0 ? "Monthly" : "Other";

const NewOrganization = () => {
    const navigate = useNavigate();
    const [openSeatDetailDialog, setOpenSeatDetailsDialog] = useState<boolean>(false);
    const [seatDetails, setSeatDetails] = useState<SeatUsage[]>([]);
    const [showCancelSubscriptionDialog, setShowCancelSubscriptionDialog] = useState(false);
    const [orgActivePlans, setOrgActivePlans] = useState<OrgPlanHistory[]>([]);
    const [selectedSubscriptionIds, setSelectedSubscriptionIds] = useState<string[]>([]);
    const [isLoadingOrgRechargeHistory, setIsLoadingOrgRechargeHistory] = useState(false);
    const [isCancellingSubscription, setIsCancellingSubscription] = useState(false);
    const [state, dispatchLocal] = useReducer(componentReducer, componentInitialState);

    const dispatch = useDispatch()
    const NewOrganizations: NewOrg[] = useSelector(selector.newOrganizations)
    const LoadingOrganizations: boolean = useSelector(selector.loadingOrganizations)
    const IsMobileDevice: boolean = useSelector(selector.isMobileDevice)
    const [filterOptions, setFilterOptions] = useState<CustomFilterCategory[]>([]);
    const [ShowFilterOptions, setShowFilterOptions] = useState<boolean>(false);
    const [anchorEl, setAnchorEl] = useState<Element | null>(null);
    const [selectedFilterTypes, setSelectedFilterTypes] = useState<string[]>([]);

    useEffect(() => {
        if (NewOrganizations?.length === 0 && !LoadingOrganizations) {
            refreshData();
        }
        LoadNewOrganizations(dispatch);
        setHeaderTitle(pageTitles.Organizations)
        setRefreshHandler(refreshData)
        return setHeaderTitle
    }, []);

    useEffect(() => {
        setHeaderCount(NewOrganizations?.length)
        initialFilterOptions()
    }, [NewOrganizations]);

    const refreshData = () => {
        // LoadOrganizations(dispatch);
        LoadNewOrganizations(dispatch)
        if (!!state.columnApi) {
            state.columnApi?.resetColumnState()
        }
        if (!!state.gridApi) {
            state.gridApi?.refreshCells({ force: true })
        }
        state.gridApi?.deselectAll();
    };

    const handleRecharge = (Org: NewOrg) => {
        navigate(AppRoutes.NEW_RECHARGE, { state: { Organization: Org, OrganizationId: Org.ID } });
    }

    const handleAddonRecharge = (Org: NewOrg) => {
        navigate(AppRoutes.NEW_RECHARGE, { state: { Organization: Org, OrganizationId: Org.ID, IsAddonRecharge: true } });
    }

    const GoToPlanHistory = (id: string) => {
        navigate({
            pathname: AppRoutes.ORGANIZATION_PLAN_HISTORY.replace(':orgId', id),
        })
    }

    const closeCancelSubscriptionDialog = () => {
        setShowCancelSubscriptionDialog(false);
        setOrgActivePlans([]);
        setSelectedSubscriptionIds([]);
        setIsLoadingOrgRechargeHistory(false);
        setIsCancellingSubscription(false);
    };

    const initiateCancelSubscription = (org: NewOrg) => {
        setShowCancelSubscriptionDialog(true);
        setIsLoadingOrgRechargeHistory(true);

        OrganizationService.GetNewOrganizationRechargeHistory(org.ID)
            .then((res) => {
                const plans = (res.data || []).map(MapToOrgPlanHistory).filter(x => x.StatusType === RechargeStatus.ONGOING && x.PlanType === PlanType.TIER && x.SubscriptionId);

                setOrgActivePlans(plans);
                setSelectedSubscriptionIds(plans.length === 1 ? [plans[0].SubscriptionId] : []);
            })
            .catch(() => {
                setToast(ToasterTexts.CANCEL_SUBSCRIPTION_FAILED, ToasterType.DANGER);
                closeCancelSubscriptionDialog();
            })
            .finally(() => {
                setIsLoadingOrgRechargeHistory(false);
            });
    };

    const handleSubscriptionSelection = (subscriptionId: string) => {
        setSelectedSubscriptionIds((prev) =>
            prev.includes(subscriptionId)
                ? prev.filter((id) => id !== subscriptionId)
                : [...prev, subscriptionId]
        );
    };

    const getMenuActionsForOrg = useCallback((orgData: NewOrg) => [
        {
            Name: orgData.IsDeleted ? ActionLabels.ENABLE : ActionLabels.DISABLE,
            Icon: orgData.IsDeleted ? EnableIcon : DisableIcon,
            OnClick: () => dispatchLocal({ type: 'INITIATE_ENABLE_DISABLE_ORG', payload: { org: orgData } }),
        },
        {
            Name: ActionLabels.ADDON_RECHARGE,
            Icon: RechargeIcon,
            OnClick: () => handleAddonRecharge(orgData),
            Hide: orgData.SeatUsage?.length < 1
        },
        {
            Name: ActionLabels.EDIT,
            Icon: EditIcon,
            OnClick: () => dispatchLocal({ type: 'OPEN_EDIT_ORG_DIALOG', payload: { orgId: orgData.ID } }),
        },
        {
            Name: ActionLabels.SETTINGS,
            Icon: SettingsIcon,
            OnClick: () => dispatchLocal({ type: 'OPEN_SETTINGS_DIALOG', payload: { orgId: orgData.ID } }),
        },
        {
            Name: orgData.SeatUsage?.length < 1 ? ActionLabels.RECHARGE : ActionLabels.MODIFY_RECHARGE,
            Icon: RechargeIcon,
            OnClick: () => handleRecharge(orgData),
        },
        {
            Name: ActionLabels.SEAT_HISTORY,
            Icon: TransactionHistoryIcon,
            OnClick: () => GoToPlanHistory(orgData.ID),
        },
        {
            Name: ActionLabels.SEAT_DETAILS,
            Icon: PlanDetailsIcon,
            OnClick: () => getSeatDetails(orgData.ID),
        },
        {
            Name: ActionLabels.CANCEL_SUBSCRIPTION,
            Icon: CancelSubscriptionIcon,
            OnClick: () => initiateCancelSubscription(orgData),
            Hide: orgData.SeatUsage?.length < 1,
        },
    ], []);

    const OrgGridColumnDefs: ColDef[] = useMemo(() => [
        { field: "Name", headerName: "Organization Name", cellStyle: { textOverflow: 'ellipsis', paddingTop: "15px" }, minWidth: 200, },
        {
            field: "PhoneNumberCount", headerName: "Phone Number", autoHeight: true,
            cellRenderer: (params: any) => params.data && <Link to={AppRoutes.NEW_PhONE_NUMBER} state={{ OrganizationID: params.data.ID }} className="color-vigenary-light">{params.data.PhoneNumberCount}</Link>
        },
        {
            field: "UserCount", headerName: "Users", autoHeight: true,
            cellRenderer: (params: any) => params.data && <Link to={AppRoutes.NEW_USERS} state={{ OrganizationID: params.data.ID }} className="color-vigenary-light">{params.data.UserCount}</Link>
        },
        // {
        //     field: "TotalBasicUsers", headerName: "Basic Seat count", autoHeight: true,
        //     cellRenderer: (params: any) => params.data && <div>{params.data.TotalBasicUsers}</div>
        // },
        // {
        //     field: "TotalPremiumUsers", headerName: "Premium Seat count", autoHeight: true,
        //     cellRenderer: (params: any) => params.data && <div>{params.data.TotalPremiumUsers}</div>
        // },
        {
            field: "IsYearPlan", headerName: "Cycle", autoHeight: true,
            valueGetter: (params: ValueGetterParams) => {
                return params.data.IsYearPlan === true ? "Yearly" : params.data.SeatUsage?.length > 0 ? "Monthly" : "--"
            },
            headerComponentParams: { template: selectedFilterTypes.includes("Cycle") ? `${HeaderTemplate.filterApplied(true)}` : "" },
            cellRendererSelector: (params: ICellRendererParams) => {
                if (!!params.data && params.data.SeatUsage?.length > 0) {
                    const label = params.data.IsYearPlan === true ? "Yearly" : "Monthly";
                    const icon = params.data.IsYearPlan === true ? ScheduleDailyIcon : ScheduleMonthlyIcon;
                    const Type = {
                        component: TypeRenderer,
                        params: {
                            Icon: icon, Label: label, Id: params.data.ID
                        }
                    }
                    return Type
                }
            },
        },
        {
            field: "IsDeleted", headerName: "Status",
            // headerComponentParams: {
            //     template: selectedFilterTypes.includes("Status") ? `${HeaderTemplate.filterApplied(true)}` : ""
            // },
            headerComponentParams: { template: selectedFilterTypes.includes("Status") ? `${HeaderTemplate.filterApplied(true)}` : "" },
            valueGetter: (params: any) => params.data?.IsDeleted ? "Disable" : "Enable",
            cellRendererSelector: (params: any) => {
                if (!params.data) return undefined;
                const label = params.data.IsDeleted ? "Disable" : "Enable";
                const variant = params.data.IsDeleted ? GridStatusLabelVariant.Danger : GridStatusLabelVariant.Active;
                return { component: StatusRenderer, params: { Variant: variant, Label: label, Id: params.data.ID } };
            },
            minWidth: 100, width: 100, maxWidth: 100,
        },
        {
            field: "CreatedDateTime", headerName: "Updated Date", // Assuming this is the desired date field
            valueFormatter: (params: ValueFormatterParams) => params.value ? UtilityService.convertDateToDay(params.value).toString() : "",
            sort: 'desc', hide: IsMobileDevice,
            comparator: (valueA, valueB, nodeA, nodeB) => dateComparator(nodeA?.data?.CreatedDateTime, nodeB?.data?.CreatedDateTime),
            minWidth: 150, width: 150, maxWidth: 150,
        },
        {
            field: "Action", headerName: "", maxWidth: IsMobileDevice ? 0 : 40, // Hide on mobile effectively
            pinned: "right", lockPinned: true, cellClass: "ag-cell-actions-style",
            cellRenderer: (params: ICellRendererParams) => params.data && <ActionsRenderer MenuData={getMenuActionsForOrg(params.data)} {...params} />,
            resizable: false, sortable: false, hide: IsMobileDevice,
        },
    ], [IsMobileDevice]);


    const onGridReady = useCallback((params: { api: GridApi, columnApi: ColumnApi }) => {
        dispatchLocal({ type: 'SET_GRID_API', payload: { api: params.api } });
        dispatchLocal({ type: 'SET_COLUMN_API', payload: { columnApi: params.columnApi } });
    }, []);

    const handleRowClick = useCallback((row: NewOrg) => {
        if (IsMobileDevice) dispatchLocal({ type: 'OPEN_MOBILE_MENU', payload: { org: row } });
    }, [IsMobileDevice]);

    const closeViewOrgDialog = useCallback(() => dispatchLocal({ type: 'CLOSE_VIEW_EDIT_ORG_DIALOG' }), []);
    const closeOrgSettingsDialog = useCallback(() => dispatchLocal({ type: 'CLOSE_SETTINGS_DIALOG' }), []);
    const closeMobileActionMenu = useCallback(() => dispatchLocal({ type: 'CLOSE_MOBILE_MENU' }), []);

    const handleCancelSubscription = () => {
        setIsCancellingSubscription(true);
        const selectedPlans = orgActivePlans.filter(p => selectedSubscriptionIds.includes(p.SubscriptionId));

        chargebeeService.cancelSubscription(selectedSubscriptionIds)
            .then(() => {
                setToast(ToasterTexts.CANCEL_SUBSCRIPTION_SUCCESS, ToasterType.SUCCESS);
                closeCancelSubscriptionDialog();
                refreshData();
            })
            .catch((err) => {
                const errors = err?.response?.data?.errors;
                const message = Array.isArray(errors)
                    ? errors?.map(error => {
                        const plan = selectedPlans.find(p => p.SubscriptionId === error.subscription_id);
                        return `${plan?.PlanName ?? error.subscription_id}: ${error.message}`;
                    }).join(", ")
                    : ToasterTexts.CANCEL_SUBSCRIPTION_FAILED;
                setToast(message || ToasterTexts.CANCEL_SUBSCRIPTION_FAILED, ToasterType.DANGER);
                setIsCancellingSubscription(false);
            });
    };

    const handleConfirmDialog = (isConfirmed: boolean): void => {
        if (!isConfirmed) {
            dispatchLocal({ type: 'CLOSE_CONFIRM_DIALOG' });
            return;
        }
        if (state.confirmDialogClickRowId) {
            dispatchLocal({ type: 'SET_CONFIRM_DIALOG_LOADING', payload: true });
            const orgName = NewOrganizations?.find(o => o.ID === state.confirmDialogClickRowId)?.Name || '';
            OrganizationService.deleteOrganizations(state.confirmDialogClickRowId)
                .then(() => {
                    setToast(state.isConfirmDialogEnableAction ? ToasterTexts.ORGANIZATION_ENABLE_SUCCESS.replace('<Name>', orgName) : ToasterTexts.ORGANIZATION_DISABLE_SUCCESS.replace('<Name>', orgName), ToasterType.SUCCESS);
                    refreshData();
                })
                .catch(() => { })
                .finally(() => {
                    dispatchLocal({ type: 'CLOSE_CONFIRM_DIALOG' });
                });
        }
    }

    const AdminOrganizations = NewOrganizations?.filter(x => x.Name !== "Admin")

    const searchOrganizationsInGrid = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        state.gridApi?.setQuickFilter(e.target.value);
    }, [state.gridApi]);

    const addOrg = useCallback(() => dispatchLocal({ type: 'OPEN_ADD_ORG_DIALOG' }), []);

    const getSeatDetails = (orgId) => {
        setOpenSeatDetailsDialog(true);
        OrganizationService.getSeatDetails(orgId).then((res) => {
            setSeatDetails(res.data.seatUsage.map(mapToSeatUsage))
        }).catch((err) => { })
    }

    const closeOpenSeatDetailsDialog = () => {
        setSeatDetails([]);
        setOpenSeatDetailsDialog(false)
    }

    const initialFilterOptions = () => {
        const filterData: CustomFilterCategory[] = [
            {
                Id: 1,
                Title: "Status",
                IsSelectedAll: false,
                Options: _(NewOrganizations).map((row: NewOrg): CustomFilterOption => {
                    return {
                        Key: "IsDeleted",
                        Id: row.ID,
                        Label: row.IsDeleted === true ? "Disable" : row.IsDeleted === false ? "Enable" : null,
                        Value: row.IsDeleted,
                        IsSelected: false,
                    }
                }).unionBy("Label")
                    .value(),
            },
            {
                Id: 2,
                Title: "Cycle",
                IsSelectedAll: false,
                Options: _(NewOrganizations).map((row: NewOrg): CustomFilterOption => {
                    return {
                        Key: "Cycle",
                        Id: row.ID,
                        Label: getCycleValue(row),
                        Value: getCycleValue(row),
                        IsSelected: false,
                    }
                }).unionBy("Label")
                    .value(),
            }
        ]
        setFilterOptions(filterData)
    }

    const handleClickFilter = (event: React.MouseEvent<HTMLButtonElement>) => {
        if (IsMobileDevice) {
            setShowFilterOptions(true);
        }
        else {
            setAnchorEl(event.currentTarget)
        }
    }

    const handleCloseFilter = () => {
        setAnchorEl(null);
        setShowFilterOptions(false);
    }

    const handleApplyFilters = (filters) => {
        setFilterOptions(filters);
        const { result } = UtilityService.getFilterTypes(filters);
        setSelectedFilterTypes(result)        
    };

    return (
        <div className="d-flex flex-direction h-p-100 o-hidden justify-between">
            <PageHeaderComponent
                Reload={() => LoadOrganizations(dispatch)}
                Actions={[{
                    OnClick: () => addOrg(),
                    ButtonLabel: buttonLabels.ADD_ORGANIZATION,
                    Icon: AddOrgIcon,
                    Variant: "primary-btn",
                }]}
                PlaceHolder={IsMobileDevice ? "Search..." : "Find an Organization..."}
                onSearch={searchOrganizationsInGrid}
                GridTitle={""}
                Count={AdminOrganizations.length}
                ShowFilterIcon={true}
                IsFilterApplied={filterOptions.flatMap(x => x.Options.filter(x => x.IsSelected)).length}
                HandleFilter={handleClickFilter}
            />
            <GridComponent
                ColumnDefs={OrgGridColumnDefs}
                RowData={NewOrganizations.map((row) => ({ ...row, Cycle: getCycleValue(row) }))}
                OnGridReady={onGridReady}
                OnGridRowClick={handleRowClick}
                IsPaginationEnabled={!IsMobileDevice}
                EmptyLabel={emptyLabels.ORGANIZATION}
                LockVisible={IsMobileDevice}
                IsFilterPresent={true}
                gridApi={state.gridApi}
                filterOptions={filterOptions}
            />

            {state.showViewOrEditOrgDialog && !IsMobileDevice &&
                <ViewOrganization
                    SelectedOrganizationId={state.selectedOrganizationIdForViewOrEdit}
                    ViewOrganization={state.showViewOrEditOrgDialog}
                    handleClose={closeViewOrgDialog}
                    IsEdit={state.isEditModeOrgDialog}
                    SelectedOrganization={NewOrganizations.find((x) => x.ID === state.selectedOrganizationIdForViewOrEdit)}
                />
            }

            {state.showSettingsDialog && !IsMobileDevice &&
                <OrgSettings
                    onClose={closeOrgSettingsDialog}
                    settings={state.showSettingsDialog}
                    OrgId={state.settingsOrgId}
                />
            }

            {state.showConfirmDialog &&
                <ConfirmDialog
                    onClose={handleConfirmDialog}
                    Show={state.showConfirmDialog}
                    Text={state.confirmDialogText}
                    ButtonName={state.confirmDialogButtonName}
                    IsMobileDevice={IsMobileDevice}
                    Loading={state.isConfirmDialogLoading}
                />
            }

            {state.showMobileMenu && state.editingOrganization &&
                <Dialog fullWidth onClose={closeMobileActionMenu} open={state.showMobileMenu} BackdropProps={{ style: { backgroundColor: "transparent" } }} classes={{ paper: "br-2 dialogue-shadow w-p-70 bg-color-menu-bg" }}>
                    <DialogTitle className="dialog-header">
                        <div className="h1 color-display-title d-flex">
                            {state.editingOrganization?.Name}
                            <Tooltip title='Close' placement='bottom'><span className={"ml-auto"}><CancelIcon className={"c-pointer"} onClick={closeMobileActionMenu} /></span></Tooltip>
                        </div>
                    </DialogTitle>
                    <DialogContent className={"mln-1 o-hidden pt-0"} >
                        <MenuItemRenderer MenuData={getMenuActionsForOrg(state.editingOrganization)} OnClick={closeMobileActionMenu} />
                    </DialogContent>
                </Dialog>
            }

            {openSeatDetailDialog &&
                <Dialog fullWidth onClose={closeOpenSeatDetailsDialog} open={openSeatDetailDialog} BackdropProps={{ style: { backgroundColor: "transparent" } }} classes={{ paper: "br-2 dialogue-shadow w-p-50 " }}>
                    <DialogTitle className="dialog-header bb-primary-divider-4">
                        <div className="h1 color-display-title d-flex ">
                            {"Seat Details"}
                            <Tooltip title='Close' placement='bottom'><span className={"ml-auto"}><CancelIcon className={"c-pointer"} onClick={closeOpenSeatDetailsDialog} /></span></Tooltip>
                        </div>
                    </DialogTitle>
                    <DialogContent className={"o-hidden pt-0"} >
                        <Grid container className='d-flex align-center justify-center py-2'>
                            {seatDetails.map(x =>
                                <Grid item md={6} className='h3-regular color-primary-accent'>
                                    <span className='color-account-billing-text-gray'>{x.SeatType} :</span>
                                    <span className='ml-2'>{x.Users.Used}/{x.Users.Total}</span>
                                </Grid>
                            )}
                        </Grid>
                    </DialogContent>
                    <DialogActions className='pb-2'>
                        <ButtonComponent OnClick={() => closeOpenSeatDetailsDialog()} ButtonLabel={buttonLabels.CANCEL} ClassName={"mr-2"} Variant={"secondary-btn-small"} />
                    </DialogActions>
                </Dialog>
            }

            {
                !IsMobileDevice && Boolean(anchorEl) &&

                <Popover
                    open={Boolean(anchorEl)}
                    anchorEl={anchorEl}
                    onClose={handleCloseFilter}
                    anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                    }}
                    classes={{ paper: `min-w-40 ${ShowFilterOptions ? "min-h-40" : ""}` }}
                // BackdropProps={{
                //     invisible: true,
                //     onClick: (event) => event.stopPropagation(),
                // }}
                >
                    <FilterComponent
                        Filters={filterOptions}
                        HandleApplyFilters={handleApplyFilters}
                        OnClose={handleCloseFilter}
                    />
                </Popover>
            }

            {showCancelSubscriptionDialog &&
                <Dialog
                    fullWidth
                    open={showCancelSubscriptionDialog}
                    onClose={closeCancelSubscriptionDialog}
                    BackdropProps={{ style: { backgroundColor: "transparent" } }}
                    classes={{ paper: `br-2 dialogue-shadow ${IsMobileDevice ? "w-p-90" : "w-p-50"}` }}
                >
                    <DialogTitle className="dialog-header bb-primary-divider-4">
                        <div className="h1 color-display-title d-flex">
                            {ActionLabels.CANCEL_SUBSCRIPTION}
                            <Tooltip title='Close' placement='bottom'>
                                <span className={"ml-auto"}>
                                    <CancelIcon className={"c-pointer"} onClick={closeCancelSubscriptionDialog} />
                                </span>
                            </Tooltip>
                        </div>
                    </DialogTitle>
                    <DialogContent className="pt-2">
                        <div className="h4-regular color-menu-list-font-color mb-2">
                            {!!orgActivePlans?.length && "Select Plans to Cancel Subscription"}
                        </div>
                        {isLoadingOrgRechargeHistory ?
                            <div className="h4-regular color-primary-accent py-2">Loading plans...</div>
                            : !orgActivePlans?.length ?
                                <div className="h4-regular color-primary-accent py-2">There are no active plans to cancel.</div>
                                : <div className="d-flex flex-direction">
                                    {orgActivePlans.map((plan) =>
                                        <div key={plan.SubscriptionId} className="d-flex align-center py-1 c-pointer">
                                            <Checkbox
                                                checked={selectedSubscriptionIds.includes(plan.SubscriptionId)}
                                                onChange={() => handleSubscriptionSelection(plan.SubscriptionId)}
                                                classes={{ checked: "selected-checkbox" }}
                                                disabled={isCancellingSubscription}
                                            />
                                            <div className="d-flex flex-direction">
                                                <span className="h4-regular color-primary-accent">{plan.PlanName || plan.SubscriptionId}</span>
                                                <span className="h6-regular color-account-billing-text-gray">
                                                    {plan.Quantity ? `Qty ${plan.Quantity}` : ""}
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                        }
                    </DialogContent>
                    <DialogActions className="pb-2">
                        <ButtonComponent
                            OnClick={closeCancelSubscriptionDialog}
                            ButtonLabel={buttonLabels.CANCEL}
                            ClassName="mr-2"
                            Variant="secondary-btn-small"
                        />
                        <ButtonComponent
                            OnClick={handleCancelSubscription}
                            ButtonLabel={buttonLabels.CANCEL_SUBSCRIPTION}
                            Variant={selectedSubscriptionIds.length && !isLoadingOrgRechargeHistory ? "primary-btn-small" : "disabled-btn-small"}
                            Loading={isCancellingSubscription}
                        />
                    </DialogActions>
                </Dialog>
            }
        </div>
    )
}

export default NewOrganization

// Reducer and State Management
interface ComponentState {
    gridApi: GridApi | null;
    columnApi: ColumnApi | null;
    showMobileMenu: boolean;
    showSettingsDialog: boolean;
    editingOrganization?: NewOrg;
    showViewOrEditOrgDialog: boolean;
    isEditModeOrgDialog: boolean;
    settingsOrgId: string
    selectedOrganizationIdForViewOrEdit?: string;
    showConfirmDialog: boolean;
    confirmDialogClickRowId: string;
    confirmDialogText: string;
    dialogAction: string;
    confirmDialogButtonName: string;
    isConfirmDialogEnableAction: boolean;
    isConfirmDialogLoading: boolean;
}

type ComponentAction =
    | { type: 'OPEN_ADD_ORG_DIALOG' }
    | { type: 'SET_GRID_API'; payload: { api: GridApi } }
    | { type: 'SET_COLUMN_API'; payload: { columnApi: ColumnApi } }
    | { type: 'OPEN_MOBILE_MENU'; payload: { org: NewOrg } }
    | { type: 'CLOSE_MOBILE_MENU' }
    | { type: 'OPEN_SETTINGS_DIALOG'; payload: { orgId: string } }
    | { type: 'OPEN_EDIT_ORG_DIALOG'; payload: { orgId: string } }
    | { type: 'INITIATE_ENABLE_DISABLE_ORG'; payload: { org: OrganizationModel } }
    | { type: 'CLOSE_VIEW_EDIT_ORG_DIALOG' }
    | { type: 'CLOSE_SETTINGS_DIALOG' }
    | { type: 'SET_CONFIRM_DIALOG_LOADING'; payload: boolean }
    | { type: 'CLOSE_CONFIRM_DIALOG' }

const componentInitialState: ComponentState = {
    gridApi: null,
    columnApi: null,
    showMobileMenu: false,
    editingOrganization: undefined,
    showSettingsDialog: false,
    showViewOrEditOrgDialog: false,
    isEditModeOrgDialog: false,
    settingsOrgId: undefined,
    selectedOrganizationIdForViewOrEdit: undefined,
    showConfirmDialog: false,
    confirmDialogClickRowId: "",
    confirmDialogText: "",
    dialogAction: "",
    confirmDialogButtonName: "",
    isConfirmDialogEnableAction: false,
    isConfirmDialogLoading: false,
}

function componentReducer(state: ComponentState, action: ComponentAction): ComponentState {
    switch (action.type) {
        case 'OPEN_ADD_ORG_DIALOG':
            return { ...state, showViewOrEditOrgDialog: true, isEditModeOrgDialog: false, selectedOrganizationIdForViewOrEdit: "" };
        case 'SET_GRID_API':
            return { ...state, gridApi: action.payload.api };
        case 'SET_COLUMN_API':
            return { ...state, columnApi: action.payload.columnApi };
        case 'OPEN_MOBILE_MENU':
            return { ...state, showMobileMenu: true, editingOrganization: action.payload.org };
        case 'CLOSE_MOBILE_MENU':
            return { ...state, showMobileMenu: false, editingOrganization: undefined };
        case 'OPEN_SETTINGS_DIALOG':
            return { ...state, showSettingsDialog: true, settingsOrgId: action.payload.orgId, showMobileMenu: false };
        case 'CLOSE_SETTINGS_DIALOG':
            return { ...state, showSettingsDialog: false, settingsOrgId: "" };
        case 'OPEN_EDIT_ORG_DIALOG':
            return { ...state, showViewOrEditOrgDialog: true, isEditModeOrgDialog: true, selectedOrganizationIdForViewOrEdit: action.payload.orgId, showMobileMenu: false };
        case 'CLOSE_VIEW_EDIT_ORG_DIALOG':
            return { ...state, showViewOrEditOrgDialog: false, isEditModeOrgDialog: false, selectedOrganizationIdForViewOrEdit: "" };
        case 'INITIATE_ENABLE_DISABLE_ORG':
            const isEnabling = action.payload.org.IsDeleted; // If IsDeleted is true, action is to Enable
            return {
                ...state,
                showConfirmDialog: true,
                confirmDialogClickRowId: action.payload.org.ID,
                confirmDialogText: isEnabling ? appNote.ORG_ENABLE : appNote.ORG_DISABLE,
                dialogAction: isEnabling ? ActionLabels.ENABLE : ActionLabels.DISABLE, // Text for the action button itself
                confirmDialogButtonName: isEnabling ? ActionLabels.ENABLE : ActionLabels.DISABLE,
                isConfirmDialogEnableAction: isEnabling,
                isConfirmDialogLoading: false,
                showMobileMenu: false,
            };
        case 'SET_CONFIRM_DIALOG_LOADING':
            return { ...state, isConfirmDialogLoading: action.payload };
        case 'CLOSE_CONFIRM_DIALOG':
            return {
                ...state,
                showConfirmDialog: false,
                confirmDialogClickRowId: "",
                dialogAction: "",
                confirmDialogText: "",
                confirmDialogButtonName: "",
                isConfirmDialogLoading: false,
            };
        default:
            throw new Error(`Unhandled action type`);
    }
}